import React, { useState, useEffect, useRef } from "react";
import { useFormValidation } from "../../../hooks/useFormValidation";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { useNavigate } from "react-router-dom";
import { InputField } from "../../Common/FormElements/InputField";
import { TextareaField } from "../../Common/FormElements/TextareaField";
import { SelectField } from "../../Common/FormElements/SelectField";

import {
  manageAppSetting,
  fetchAppSetting,
} from "../../../services/appSettingService";

const ManageAppSettings = () => {
  // ✅ Move all hooks to the top
  const navigate = useNavigate();
  const PageLevelAccessurl = "app-setting";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);
  const paymentType = [{ id: "RazorPay", gatewayTypeName: "RazorPay" }];

  // ✅ Form validation hook
  const {
    formErrors,
    isFormValid,
    registerField,
    validateAllFields,
    clearErrors,
    touchAllFields,
    getFieldLabel,
  } = useFormValidation();

  // ✅ Create refs for each field
  const paymentGatewayTypeRef = useRef(null);
  const maxCartValueRef = useRef(null);
  const standardShippingCostRef = useRef(null);
  const mailFromRef = useRef(null);
  const mailFromNameRef = useRef(null);
  const userEmailIdRef = useRef(null);
  const userPasswordRef = useRef(null);
  const hostRef = useRef(null);
  const portRef = useRef(null);
  const toEmailAddressesRef = useRef(null);
  const ccEmailAddressesRef = useRef(null);
  const bccEmailAddressesRef = useRef(null);

  // ✅ Initialize all state properly
  const [formValues, setFormValues] = useState({
    PaymentGatewayType: "",
    MaxCartValueForFreeShipping: "",
    StandardShippingCost: "",
    MailFrom: "",
    MailFromName: "",
    UserEmailId: "",
    UserPassword: "",
    Host: "",
    Port: "",
    EnableSSL: false,
    ToEmailAddresses: "",
    CcEmailAddresses: "",
    BccEmailAddresses: "",
  });

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // ✅ Register all fields
  useEffect(() => {
    registerField(
      "PaymentGatewayType",
      paymentGatewayTypeRef,
      "Payment Gateway Type"
    );
    registerField(
      "MaxCartValueForFreeShipping",
      maxCartValueRef,
      "Max Cart Value for Free Shipping"
    );
    registerField(
      "StandardShippingCost",
      standardShippingCostRef,
      "Standard Shipping Cost"
    );
    registerField("MailFrom", mailFromRef, "Mail From");
    registerField("MailFromName", mailFromNameRef, "Mail From Name");
    registerField("UserEmailId", userEmailIdRef, "User Email ID");
    registerField("UserPassword", userPasswordRef, "User Password");
    registerField("Host", hostRef, "Host");
    registerField("Port", portRef, "Port");
    registerField(
      "ToEmailAddresses",
      toEmailAddressesRef,
      "To Email Addresses"
    );
    registerField(
      "CcEmailAddresses",
      ccEmailAddressesRef,
      "CC Email Addresses"
    );
    registerField(
      "BccEmailAddresses",
      bccEmailAddressesRef,
      "BCC Email Addresses"
    );
  }, [registerField]);

  // Add this useEffect to validate after formValues change
  useEffect(() => {
    // Only validate if form has been touched/submitted before
    if (Object.keys(formErrors).length > 0) {
      const timeoutId = setTimeout(() => {
        validateAllFields();
      }, 100); // Small delay to ensure refs are updated

      return () => clearTimeout(timeoutId);
    }
  }, [formValues, validateAllFields, formErrors]);

  // ✅ Fix page access check with proper dependencies
  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess || !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, navigate]);

  // ✅ Load existing app settings on component mount
  useEffect(() => {
    const loadAppSettings = async () => {
      try {
        setIsLoading(true);
        const response = await fetchAppSetting();
        if (response?.isSuccess && response?.result) {
          setFormValues({
            PaymentGatewayType: response.result.paymentGatewayType || "",
            MaxCartValueForFreeShipping:
              response.result.maxCartValueForFreeShipping?.toString() || "",
            StandardShippingCost:
              response.result.standardShippingCost?.toString() || "",
            MailFrom: response.result.mailFrom || "",
            MailFromName: response.result.mailFromName || "",
            UserEmailId: response.result.userEmailId || "",
            UserPassword: response.result.userPassword || "",
            Host: response.result.host || "",
            Port: response.result.port || "",
            EnableSSL: response.result.enableSSL || false,
            ToEmailAddresses: response.result.toEmailAddresses || "",
            CcEmailAddresses: response.result.ccEmailAddresses || "",
            BccEmailAddresses: response.result.bccEmailAddresses || "",
          });
        }
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAppSettings();
  }, []);

  const handleInputChange = (e) => {
    const { id, type, value, checked } = e.target;
    const inputValue = type === "checkbox" ? checked : value;

    setFormValues((prev) => ({
      ...prev,
      [id]: inputValue,
    }));
  };

  // ✅ Reset all form fields
  const resetAllFields = () => {
    const fieldRefs = {
      paymentGatewayTypeRef,
      maxCartValueRef,
      standardShippingCostRef,
      mailFromRef,
      mailFromNameRef,
      userEmailIdRef,
      userPasswordRef,
      hostRef,
      portRef,
      toEmailAddressesRef,
      ccEmailAddressesRef,
      bccEmailAddressesRef,
    };

    Object.values(fieldRefs).forEach((ref) => {
      if (ref.current && ref.current.reset) {
        ref.current.reset();
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Touch all fields to show validation errors
    touchAllFields();

    // ✅ Validate all fields using refs
    const isValid = validateAllFields();

    if (!isValid) {
      toast.error("Please fix all validation errors before submitting.");
      return;
    }

    try {
      setIsButtonDisabled(true);

      // Convert numeric fields to proper format for the API
      const payload = {
        ...formValues,
        MaxCartValueForFreeShipping:
          parseFloat(formValues.MaxCartValueForFreeShipping) || 0,
        StandardShippingCost: parseFloat(formValues.StandardShippingCost) || 0,
      };

      const responseData = await manageAppSetting(payload);

      if (responseData?.data?.isSuccess) {
        toast.success(
          responseData.data.statusMessage ||
            "App settings updated successfully!"
        );
      } else {
        toast.error(
          responseData?.data?.statusMessage || "Failed to update app settings"
        );
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <ComponentHeader title="Manage App Settings" />
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "300px" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ComponentHeader title="Manage App Settings" />
      {/* ✅ Optional: Show form-level validation status */}
      {Object.keys(formErrors).length > 0 && (
        <div className="alert alert-warning mb-3">
          <strong>Please fix the following errors:</strong>
          <ul className="mb-0 mt-2">
            {Object.entries(formErrors).map(([fieldId, error]) => (
              <li key={fieldId}>
                <strong>{getFieldLabel(fieldId)}:</strong> {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} method="POST">
        <div className="row">
          {/* Payment & Shipping Settings */}
          <div className="col-xxl-6">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Payment & Shipping Settings</h5>
              </div>
              <div className="card-body p-4">
                <div className="row">
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <SelectField
                      ref={paymentGatewayTypeRef}
                      label="Payment Gateway Type"
                      id="PaymentGatewayType"
                      value={formValues.PaymentGatewayType}
                      onChange={handleInputChange}
                      placeholder="Enter Payment Gateway Type"
                      options={paymentType}
                      optionValueKey="id"
                      optionLabelKey="gatewayTypeName"
                      required={true}
                    />
                  </div>

                  <div className="col-lg-12">
                    <InputField
                      ref={maxCartValueRef}
                      label="Max Cart Value for Free Shipping"
                      id="MaxCartValueForFreeShipping"
                      value={formValues.MaxCartValueForFreeShipping}
                      onChange={handleInputChange}
                      placeholder="Enter maximum cart value"
                      validationType="decimal"
                      required={true}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-12">
                    <InputField
                      ref={standardShippingCostRef}
                      label="Standard Shipping Cost"
                      id="StandardShippingCost"
                      value={formValues.StandardShippingCost}
                      onChange={handleInputChange}
                      placeholder="Enter standard shipping cost"
                      validationType="decimal"
                      required={true}
                      showError={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Email Configuration */}
          <div className="col-xxl-6">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Email Configuration</h5>
                <div className="d-flex align-items-center">
                  <label htmlFor="EnableSSL" className="form-label pe-3">
                    Enable SSL
                  </label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="EnableSSL"
                      name="EnableSSL"
                      checked={formValues.EnableSSL}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="EnableSSL">
                      {formValues.EnableSSL ? "Enabled" : "Disabled"}
                    </label>
                  </div>
                </div>
              </div>
              <div className="card-body p-4">
                <div className="row">
                  <div className="col-lg-6">
                    <InputField
                      ref={mailFromRef}
                      label="Mail From"
                      id="MailFrom"
                      value={formValues.MailFrom}
                      placeholder="Enter sender email"
                      onChange={handleInputChange}
                      required={true}
                      validationType="email"
                      maxLength={3000}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-6">
                    <InputField
                      ref={mailFromNameRef}
                      label="Mail From Name"
                      id="MailFromName"
                      value={formValues.MailFromName}
                      placeholder="Enter sender name"
                      onChange={handleInputChange}
                      required={true}
                      maxLength={3000}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-6">
                    <InputField
                      ref={userEmailIdRef}
                      label="User  ID"
                      id="UserEmailId"
                      value={formValues.UserEmailId}
                      placeholder="Enter authentication email"
                      onChange={handleInputChange}
                      required={true}
                      maxLength={3000}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-6">
                    <InputField
                      ref={userPasswordRef}
                      label="User Password"
                      id="UserPassword"
                      type="password"
                      value={formValues.UserPassword}
                      placeholder="Enter email password"
                      onChange={handleInputChange}
                      required={true}
                      maxLength={3000}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-6">
                    <InputField
                      ref={hostRef}
                      label="Host"
                      id="Host"
                      value={formValues.Host}
                      placeholder="Enter email server host"
                      onChange={handleInputChange}
                      required={true}
                      maxLength={3000}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-6">
                    <InputField
                      ref={portRef}
                      label="Port"
                      id="Port"
                      value={formValues.Port}
                      placeholder="Enter port number"
                      onChange={handleInputChange}
                      required={true}
                      validationType="numberonly"
                      maxLength={3000}
                      showError={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Email Recipients */}
          <div className="col-xxl-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Email Recipients</h5>
              </div>
              <div className="card-body p-4">
                <div className="row">
                  <div className="col-lg-4">
                    <TextareaField
                      ref={toEmailAddressesRef}
                      label="To Email Addresses"
                      id="ToEmailAddresses"
                      value={formValues.ToEmailAddresses}
                      onChange={handleInputChange}
                      placeholder="Enter comma-separated email addresses"
                      required={true}
                      showCharCount={true}
                      maxLength={3000}
                      rows={3}
                    />
                  </div>
                  <div className="col-lg-4">
                    <TextareaField
                      ref={ccEmailAddressesRef}
                      label="CC Email Addresses"
                      id="CcEmailAddresses"
                      value={formValues.CcEmailAddresses}
                      onChange={handleInputChange}
                      placeholder="Enter comma-separated CC email addresses"
                      required={false}
                      showCharCount={true}
                      maxLength={3000}
                      rows={3}
                    />
                  </div>
                  <div className="col-lg-4">
                    <TextareaField
                      ref={bccEmailAddressesRef}
                      label="BCC Email Addresses"
                      id="BccEmailAddresses"
                      value={formValues.BccEmailAddresses}
                      onChange={handleInputChange}
                      placeholder="Enter comma-separated BCC email addresses"
                      required={false}
                      showCharCount={true}
                      maxLength={3000}
                      rows={3}
                    />
                  </div>
                </div>

                <div className="text-start mt-4">
                  <button
                    type="submit"
                    className="btn btn-secondary"
                    disabled={isButtonDisabled || !isFormValid}
                  >
                    {isButtonDisabled ? "Updating..." : "Update Settings"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default ManageAppSettings;
